import { upperFirst } from 'lodash-es'
import { type Dispatch, type SetStateAction, useState } from 'react'
import toast from 'react-hot-toast'

import { FvButton, FvDate, FvNum, Icon } from '@fv/client-components'
import {
  getEquipmentLabel,
  totalShipmentWeight,
  useCurrencyOptions,
} from '@fv/client-core'
import type { FullShipment, Quote } from '@fv/client-types'
import type { Currency } from '@fv/models'

import { HasPermission } from '@/features/auth'
import { HideForVendor } from '@/features/auth/HasRole'
import {
  useWorkflowEnabled,
  WorkflowEnabled,
} from '@/features/auth/HasWorkflow'
import { BidExpirationEditor } from '@/features/bid-expiration/BidExpirationEditor'
import { ChargeList } from '@/features/charges/ChargeList'
import { ChargesSlider } from '@/features/charges/ChargesSlider'
import { useChargeList } from '@/features/charges/useChargeList'
import { ComparableLoadsInline } from '@/features/comparable-loads/ComparableLoadsInline'
import { quoteFuncs } from '@/features/quote/quoteFuncs'
import { LoadUserDisplay } from '@/features/shipment-list/LoadUserDisplay'
import { usePrettyNumber, useTypeNames } from '@/hooks/settings'
import { useUpdateTargetPrice } from '@/hooks/spotQuotes'

import { DividedContent } from '../../DividedContent'
import { InputAdornment, InputGroup } from '../../inputs/InputGroup'
import { SelectField } from '../../inputs/SelectField'

type PanelHeaderNavProps = {
  setShowingRates: Dispatch<SetStateAction<boolean>>
}

type PanelHeaderBodyProps = {
  load: FullShipment
  hasAnyResponses?: number
}

export const PanelHeaderNav = ({ setShowingRates }: PanelHeaderNavProps) => (
  <DividedContent>
    <FvButton onClick={() => setShowingRates(false)}>
      <Icon icon="arrow-to-left" />
      <span>Back</span>
    </FvButton>
    <h6 className="mb-0">
      <Icon icon="truck-moving" className="text-fv-gray-dark" />
      <span>Select new carrier / edit charges</span>
    </h6>
  </DividedContent>
)

export const PanelHeaderBody = ({
  load,
  hasAnyResponses,
}: PanelHeaderBodyProps) => {
  const {
    equipment,
    isLiveLoad,
    locations,
    refNums,
    selectedQuote,
    targetPrice,
  } = load
  const totalWeight = totalShipmentWeight(load)
  const { equipmentName } = useTypeNames()
  const prettyNumber = usePrettyNumber()
  const [updatedTargetPrice, setTargetPrice] = useState(targetPrice)
  const [updatedTargetPriceCurrency, setTargetPriceCurrency] =
    useState<Currency>(load.targetPriceCurrency || 'usd')
  const chargeList = useChargeList(selectedQuote)
  const currencyOptions = useCurrencyOptions(locations)
  const workflowEnabled = useWorkflowEnabled(load.workflow)

  const equipmentLabel = getEquipmentLabel({
    altEquipmentNames: equipment.alternateTypes?.map(
      (t, i) => `${equipmentName(t)} (A${i + 1})`,
    ),
    equipmentName: equipmentName(
      selectedQuote?.equipmentType ?? equipment.type,
    ),
    isAwarded: !!selectedQuote,
  })

  const sendTargetPrice = useUpdateTargetPrice()
  const [showTargetField, setShowTargetField] = useState(false)

  const updateTargetPrice = () => {
    sendTargetPrice.mutate(
      {
        loadId: load.loadId,
        targetPrice: updatedTargetPrice,
        targetPriceCurrency: updatedTargetPriceCurrency,
      },
      {
        onSuccess: () => {
          setShowTargetField(false)
          setTargetPrice(updatedTargetPrice)
          toast.success('Target price has been updated.')
        },
      },
    )
  }

  return (
    <div className="mb-2 !leading-[1.6rem]">
      {selectedQuote && quoteFuncs.isSpotWorkflow(selectedQuote) && (
        <div>
          <div>
            <span>
              <Icon icon="trophy" className="text-fv-gray-dark" />
              <span>Awarded - awaiting confirmation</span>
            </span>
          </div>
          <QuotePriceButton
            quote={selectedQuote}
            onClick={
              workflowEnabled && !load.isFreightCollect
                ? chargeList.toggleViewing
                : undefined
            }
          />
        </div>
      )}

      {chargeList.isViewing && (
        <ChargeList
          quote={selectedQuote}
          load={load}
          chargeState={chargeList}
        />
      )}
      <ChargesSlider chargeState={chargeList} load={load} />

      <div>
        {equipmentLabel}
        <span className="shipment-list-ancillary-details__live-load">
          {' / '}
          {isLiveLoad ? 'Live load' : 'Quote only'}
        </span>
        {refNums?.length > 0 &&
          ` / Ref# ${refNums.map(n => n.value).join(' / ')}`}
      </div>
      <div>
        {equipment.isHazardous && (
          <>
            <Icon icon="radiation-alt" className="text-fv-orange" />
            Hazardous /{' '}
          </>
        )}
        {equipment.description && `${upperFirst(equipment.description)} / `}
        {prettyNumber(totalWeight)} {equipment.weightUOM}.
      </div>
      <div>
        Quoted by <LoadUserDisplay {...load.createdBy} /> /{' '}
        <FvDate val={load.createdDate} utc format="MMM D, YYYY" />
      </div>
      <HideForVendor>
        {hasAnyResponses > 0 && !load.isFreightCollect && (
          <WorkflowEnabled workflow={load.workflow}>
            {!showTargetField ? (
              <div>
                Target price:{' '}
                {/* if no target value for bidding has been entered, we should show 'None'.
            inside the following button and it should open a text field so they can enter a target price*/}
                <FvButton
                  theme="underlined"
                  onClick={() => setShowTargetField(true)}
                >
                  {targetPrice ? (
                    <>
                      ${targetPrice}{' '}
                      {(load.targetPriceCurrency || 'usd').toUpperCase()}
                    </>
                  ) : (
                    <>none</>
                  )}
                </FvButton>
              </div>
            ) : (
              <InputGroup
                className="my-2"
                inputType="amount"
                inputProps={{
                  name: 'target-price',
                  placeholder: 'Target price',
                  onChange: e => setTargetPrice(parseFloat(e.target.value)),
                  value: updatedTargetPrice,
                  isPrice: true,
                  type: 'number',
                  min: 1,
                }}
                startContent={
                  <InputAdornment position="start" icon="dollar-sign" />
                }
                endContent={
                  <InputAdornment position="end" className="flex">
                    <SelectField
                      name="target-price-currency"
                      value={updatedTargetPriceCurrency}
                      onChange={e =>
                        setTargetPriceCurrency(e.target.value as Currency)
                      }
                      options={currencyOptions}
                      className="w-auto"
                    />
                    <FvButton
                      theme="default"
                      icon="check"
                      onClick={updateTargetPrice}
                    >
                      <span>Save</span>
                    </FvButton>
                    <FvButton
                      theme="plain"
                      icon="times"
                      onClick={() => {
                        setShowTargetField(false)
                        setTargetPrice(null)
                      }}
                    />
                  </InputAdornment>
                }
              />
            )}
            <BidExpirationEditor load={load} />
          </WorkflowEnabled>
        )}
        <WorkflowEnabled workflow={load.workflow}>
          <ComparableLoadsInline load={load} />
        </WorkflowEnabled>
      </HideForVendor>
    </div>
  )
}

type ButtonProps = {
  quote: Quote
  onClick?: () => void
}
const QuotePriceButton = ({ quote, onClick }: ButtonProps) => {
  const { canView } = useChargeList(quote)
  if (!canView || !onClick) {
    return (
      <span>
        {quote.providerName}{' '}
        <HasPermission name="view-rates">
          <FvNum
            val={quote.amount}
            currency={quote.currency}
            includeEmptyDecimals
          />
        </HasPermission>
      </span>
    )
  }

  return (
    <span>
      {quote.providerName}{' '}
      <HasPermission name="view-rates">
        <FvButton onClick={onClick} theme="underlined">
          <FvNum
            val={quote.amount}
            currency={quote.currency}
            includeEmptyDecimals
          />
        </FvButton>
      </HasPermission>
    </span>
  )
}
